import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';

// These imports are technically not allowed, but I don't want to move the types...
import type { SubdivisionStrategies } from '@studio/api/utils/subdivision';
import type { LoginMode } from '@studio/core/components/SessionManager';
import type {
  Experience,
  PublishingSite,
  VerifiedExperience,
} from '@studio/core/utils/experience';

import { type EnvironmentConfig, getEnvironmentConfig } from './environment';
import {
  type ExperienceValidationOptions,
  getValidatedExperience,
  type ValidatedExperience,
} from './experience';
import type { Mutable, Super } from './types';
import useAbortSignal from './useAbortSignal';
import useLastEqualValue from './useLastEqualValue';

/**
 * This is used to prevent creating a new RequestContext instance without extending a previous.
 * It should NOT be exported!
 */
const RequestContextSymbol = Symbol('RequestContext');

export interface RequestContext {
  readonly [RequestContextSymbol]: unknown;
  readonly signal: AbortSignal;
  readonly environment: EnvironmentConfig;
  readonly loginMode: LoginMode | null;
  /** Used by the Users API to get the ID of the logged-in User */
  readonly userId: string | null;
  readonly experience: VerifiedExperience | null;
  readonly subdivisionStrategies: SubdivisionStrategies | null;
  readonly publishingSiteId: PublishingSite | null;
  readonly meta?: object;
}

export function isRequestContext(value: unknown): value is RequestContext {
  return (
    value !== null && typeof value === 'object' && RequestContextSymbol in value
  );
}

export function isExperienceEqual(
  a: Experience | null | undefined,
  b: Experience | null | undefined,
): boolean {
  return (
    (a?.studioExperience ?? '') === (b?.studioExperience ?? '') &&
    (a?.subdivisionMarket ?? '') === (b?.subdivisionMarket ?? '') &&
    (a?.subdivisionTenant ?? '') === (b?.subdivisionTenant ?? '') &&
    (a?.publishingSite ?? '') === (b?.publishingSite ?? '') &&
    (a?.productLine ?? '') === (b?.productLine ?? '') &&
    (a?.commerceLine ?? '') === (b?.commerceLine ?? '')
  );
}

export function isRequestContextEqual(
  a: RequestContext,
  b: RequestContext,
): boolean {
  return (
    a.environment === b.environment &&
    a.loginMode === b.loginMode &&
    a.userId === b.userId &&
    a.subdivisionStrategies === b.subdivisionStrategies &&
    a.publishingSiteId === b.publishingSiteId &&
    isExperienceEqual(a.experience, b.experience)
  );
}

/** Avoid using this directly - prefer extending an injected context */
export const DEFAULT_REQUEST_CONTEXT: RequestContext = {
  [RequestContextSymbol]: undefined,
  // The default signal never aborts
  // TODO: should we use a timeout signal here instead?
  get signal() {
    const controller = new AbortController();

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (import.meta.webpackHot) {
      import.meta.webpackHot.dispose(() => {
        controller.abort();
      });
    }

    return controller.signal;
  },
  get environment() {
    return getEnvironmentConfig();
  },
  loginMode: null,
  userId: null,
  subdivisionStrategies: null,
  publishingSiteId: null,
  experience: null,
};

export const RequestContextContext = createContext<RequestContext>(
  DEFAULT_REQUEST_CONTEXT,
);

RequestContextContext.displayName = 'RequestContext';

export interface RequestContextProviderProps {
  readonly value: RequestContext;
  readonly children: ReactNode;
}

export function RequestContextProvider({
  value,
  children,
}: RequestContextProviderProps): ReactElement {
  const requestContext = useLastEqualValue(value, isRequestContextEqual);
  return (
    <RequestContextContext.Provider value={requestContext}>
      {children}
    </RequestContextContext.Provider>
  );
}

/**
 * When you want to use access the raw request context and
 * You Know What You're Doing.
 *
 * Please make sure you supply your own abort signal
 */
export function useRequestContext_UNSAFE(): RequestContext {
  return useContext(RequestContextContext);
}

/**
 * Get the current request context, with an abortsignal bound to this component's lifecycle
 */
export function useRequestContext(): RequestContext {
  const context = useContext(RequestContextContext);
  const signal = useAbortSignal();
  return useMemo(() => ({ ...context, signal }), [context, signal]);
}

export interface RequestContextValidationOptions extends ExperienceValidationOptions {}

export type ValidatedRequestContext<
  Options extends RequestContextValidationOptions,
> = {
  readonly [K in keyof RequestContext]: K extends 'experience'
    ? ValidatedExperience<RequestContext[K], Options>
    : RequestContext[K];
};

export function getValidatedRequestContext<
  const Options extends RequestContextValidationOptions,
>(
  context: RequestContext,
  options: Super<Options, RequestContextValidationOptions> | undefined,
): ValidatedRequestContext<Options> {
  let modifiedContext: Mutable<RequestContext> | undefined;

  const validatedExperience = getValidatedExperience(
    context.experience,
    options,
  );

  // Only modify the context if the experience has changed
  if (validatedExperience !== context.experience) {
    modifiedContext ??= { ...context };
    modifiedContext.experience = validatedExperience;
  }

  return (modifiedContext ?? context) as ValidatedRequestContext<Options>;
}
